// app/frontend/components/Posts/CommentsForm.tsx

import React, { useState } from 'react';
import { Textarea, Button, Group, Paper, Text, Anchor } from '@mantine/core';
import { CommentFormData } from '../../types/comment';

interface CommentsFormProps {
  postId: number;
  onCommentSubmit: (formData: CommentFormData) => Promise<void>;
  placeholder?: string;
  submitLabel?: string;
  isReply?: boolean;
  isUserSignedIn?: boolean;
}

const CommentsForm: React.FC<CommentsFormProps> = ({
  postId,
  onCommentSubmit,
  placeholder = 'Add a comment...',
  submitLabel = 'Post Comment',
  isReply = false,
  isUserSignedIn = true,
}) => {
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isUserSignedIn) {
      setError('You must be signed in to comment');
      return;
    }

    if (body.trim() === '') {
      setError('Comment cannot be empty');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onCommentSubmit({
        post_id: postId,
        body: body.trim(),
      });

      setBody(''); // Reset form after successful submission
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isUserSignedIn) {
    return (
      <Paper
        shadow="lg"
        p={30}
        mb="md"
        radius="md"
        style={{
          width: '100%',
          margin: '0 auto',
          backgroundColor: '#17171770',
        }}
      >
        <Text align="center" c="white">
          Please{' '}
          <Anchor href="/login" c="orange">
            sign in
          </Anchor>{' '}
          to leave a comment.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      p="md"
      radius="md"
      mb={isReply ? 0 : 'md'}
      style={{
        width: '100%',
        margin: '0 auto',
        backgroundColor: '#757575',
      }}
    >
      <Textarea
        placeholder={placeholder}
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        minRows={isReply ? 2 : 3}
        autosize
        required
        mb="sm"
        error={error}
      />

      <Group justify="right">
        <Button
          type="submit"
          className="clear-button"
          loading={isSubmitting}
          disabled={body.trim() === ''}
          color="orange"
        >
          {submitLabel}
        </Button>
      </Group>
    </Paper>
  );
};

export default CommentsForm;
